// crewbit-admin/src/lib/expenseMutations.ts
//
// Phase B.3.4 — Money Module RPC-routed expense mutations (admin / operator).
// Spec: docs/references/MONEY_MODULE_DATA_v1.md §5.6 / §5.7; WIRING §3.6 / §3.7.
// Mirrors crewbit-mobile/lib/expenseMutations.ts (B.3.3, commit 9d93b0b).
//
// Plain async functions, not a hook — they don't read or write React state.
// Identity (logged_by, business_id) resolves server-side via
// get_public_user_id() / get_user_business_id() inside the RPC bodies; the
// client never passes them. The wrapper (callMoneyRpc) handles error mapping.
//
// `record_expense` (M-A4-016) atomically writes three rows in a single
// transaction: `expenses` + `payment_ledger` (direction='out', entity_type='expense')
// + `activity_log`. Callers should NOT follow up with client-side payment_ledger
// or activity_log inserts — the RPC owns those.
//
// Emit-via-inheritance (Session 129, per memory feedback_emit_via_inheritance.md):
// MONEY_MUTATION_EVENT emits live at the wrapper boundary in `recordExpense` +
// `updateExpense` only (2 leaf emits). `archiveExpense` + `linkReceipt` call
// `updateExpense` internally and inherit the emit. If a future refactor breaks
// that inheritance chain, the new wrapper MUST add an explicit emit.

import { callMoneyRpc, type MoneyRpcResult } from './moneyRpc';
import { MONEY_MUTATION_EVENT } from '../hooks/useFinancialSummary';

export interface RecordExpenseArgs {
  businessId: string;
  amount: number;
  category: string;
  /** REQUIRED — caller generates at form-load time via `useState(() => uuidv4())`. */
  idempotencyKey: string;
  description?: string | null;
  vendorName?: string | null;
  receiptUrl?: string | null;
  /** ISO date 'YYYY-MM-DD'. Defaults to CURRENT_DATE server-side when null. */
  expenseDate?: string | null;
  paymentMethod?: string | null;
  linkedJobId?: string | null;
  linkedProjectId?: string | null;
  /**
   * Optional phase linkage (M-A4-020 / Session 103). When provided, RPC
   * auto-derives project_id from the phase if linkedProjectId is null;
   * if both are given, RPC validates phase belongs to project.
   */
  phaseId?: string | null;
  taxAmount?: number | null;
  deductibleAmount?: number | null;
  deductiblePercentage?: number | null;
  isHstExempt?: boolean | null;
  notes?: string | null;
  mileageKm?: number | null;
  mileageRate?: number | null;
  mileageFrom?: string | null;
  mileageTo?: string | null;
  tripId?: string | null;
  isRecurring?: boolean | null;
  recurringFrequency?: string | null;
  recurringParentId?: string | null;
  ocrData?: Record<string, unknown> | null;
  ocrConfidence?: number | null;
  aiCategorized?: boolean | null;
  receiptFilename?: string | null;
  receiptThumbnailUrl?: string | null;
  // Wave 7 — vendor entity FK (Phase 5 Migration C added column; this wave
  // threads the picker selection through the manual record path too).
  vendorId?: string | null;
}

export type RecordExpenseResult = MoneyRpcResult<{
  expense_id: string;
  ledger_id: string;
  t2125_line: string | null;
  already_created: boolean;
}>;

export async function recordExpense(args: RecordExpenseArgs): Promise<RecordExpenseResult> {
  const result = await callMoneyRpc('record_expense', {
    p_business_id: args.businessId,
    p_amount: args.amount,
    p_category: args.category,
    p_description: args.description,
    p_vendor_name: args.vendorName,
    p_receipt_url: args.receiptUrl,
    p_expense_date: args.expenseDate,
    p_payment_method: args.paymentMethod,
    p_idempotency_key: args.idempotencyKey,
    p_linked_job_id: args.linkedJobId,
    p_linked_project_id: args.linkedProjectId,
    p_tax_amount: args.taxAmount,
    p_deductible_amount: args.deductibleAmount,
    p_deductible_percentage: args.deductiblePercentage,
    p_is_hst_exempt: args.isHstExempt,
    p_notes: args.notes,
    p_mileage_km: args.mileageKm,
    p_mileage_rate: args.mileageRate,
    p_mileage_from: args.mileageFrom,
    p_mileage_to: args.mileageTo,
    p_trip_id: args.tripId,
    p_is_recurring: args.isRecurring,
    p_recurring_frequency: args.recurringFrequency,
    p_recurring_parent_id: args.recurringParentId,
    p_ocr_data: args.ocrData,
    p_ocr_confidence: args.ocrConfidence,
    p_ai_categorized: args.aiCategorized,
    p_receipt_filename: args.receiptFilename,
    p_receipt_thumbnail_url: args.receiptThumbnailUrl,
    p_phase_id: args.phaseId,
    p_vendor_id: args.vendorId,
  }) as RecordExpenseResult;
  if (result.success) {
    window.dispatchEvent(new Event(MONEY_MUTATION_EVENT));
  }
  return result;
}

export interface UpdateExpenseArgs {
  expenseId: string;
  category?: string | null;
  description?: string | null;
  amount?: number | null;
  taxAmount?: number | null;
  deductibleAmount?: number | null;
  deductiblePercentage?: number | null;
  isHstExempt?: boolean | null;
  notes?: string | null;
  paymentMethod?: string | null;
  vendorName?: string | null;
  expenseDate?: string | null;
  receiptUrl?: string | null;
  receiptFilename?: string | null;
  receiptThumbnailUrl?: string | null;
  ocrData?: Record<string, unknown> | null;
  ocrConfidence?: number | null;
  aiCategorized?: boolean | null;
  archived?: boolean | null;
  tripId?: string | null;
  linkedJobId?: string | null;
  linkedProjectId?: string | null;
  /** Optional phase linkage (M-A4-020 / Session 103). Same semantics as record. */
  phaseId?: string | null;
  // Wave 7 — vendor entity FK. NULL preserves the existing vendor_id (the
  // RPC's update gate treats NULL as a non-change, same as the other COALESCE
  // semantics). Explicit value rewires the link; explicit NULL via the picker
  // (operator clears the selection) is not currently supported by the UI.
  vendorId?: string | null;
  // Wave 8 Step 3 — per-expense tax_type. NULL preserves existing (COALESCE
  // semantics). Explicit value sets a new tax_type; clearing to NULL via the
  // picker isn't supported in the UI today.
  taxType?: string | null;
  /**
   * Optional — when provided, RPC checks for a prior call with the same key on
   * the same row inside a 1-hour window and short-circuits with
   * `already_processed: true`. Auto-save passes one stable key per form session
   * to dedup retries; ad-hoc edits can omit it.
   */
  idempotencyKey?: string | null;
}

export type UpdateExpenseResult = MoneyRpcResult<{
  expense_id: string;
  updated_fields: string[];
  already_processed?: boolean;
}>;

export async function updateExpense(args: UpdateExpenseArgs): Promise<UpdateExpenseResult> {
  const result = await callMoneyRpc('update_expense', {
    p_expense_id: args.expenseId,
    p_category: args.category,
    p_description: args.description,
    p_amount: args.amount,
    p_tax_amount: args.taxAmount,
    p_deductible_amount: args.deductibleAmount,
    p_deductible_percentage: args.deductiblePercentage,
    p_is_hst_exempt: args.isHstExempt,
    p_notes: args.notes,
    p_payment_method: args.paymentMethod,
    p_vendor_name: args.vendorName,
    p_expense_date: args.expenseDate,
    p_receipt_url: args.receiptUrl,
    p_receipt_filename: args.receiptFilename,
    p_receipt_thumbnail_url: args.receiptThumbnailUrl,
    p_ocr_data: args.ocrData,
    p_ocr_confidence: args.ocrConfidence,
    p_ai_categorized: args.aiCategorized,
    p_archived: args.archived,
    p_trip_id: args.tripId,
    p_linked_job_id: args.linkedJobId,
    p_linked_project_id: args.linkedProjectId,
    p_phase_id: args.phaseId,
    p_vendor_id: args.vendorId,
    p_tax_type: args.taxType,
    p_idempotency_key: args.idempotencyKey,
  }) as UpdateExpenseResult;
  if (result.success) {
    window.dispatchEvent(new Event(MONEY_MUTATION_EVENT));
  }
  return result;
}

/** Thin wrapper of `updateExpense` — sets archived flag. */
export async function archiveExpense(expenseId: string): Promise<UpdateExpenseResult> {
  return updateExpense({ expenseId, archived: true });
}

export interface LinkReceiptArgs {
  receiptUrl: string;
  receiptFilename?: string | null;
  receiptThumbnailUrl?: string | null;
  ocrData?: Record<string, unknown> | null;
  ocrConfidence?: number | null;
}

/** Thin wrapper of `updateExpense` — sets receipt + optional OCR fields after upload. */
export async function linkReceipt(
  expenseId: string,
  args: LinkReceiptArgs,
): Promise<UpdateExpenseResult> {
  return updateExpense({
    expenseId,
    receiptUrl: args.receiptUrl,
    receiptFilename: args.receiptFilename,
    receiptThumbnailUrl: args.receiptThumbnailUrl,
    ocrData: args.ocrData,
    ocrConfidence: args.ocrConfidence,
  });
}
